import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Optional, Self, computed, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { FcpFloatLabelComponent, FloatLabelVariant } from '../fcp-float-label';

@Component({
    selector: 'fcp-date-picker',
    imports: [CommonModule, DatePickerModule, FormsModule, FcpFloatLabelComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <ng-template #datePickerTemplate>
            <p-datepicker
                [inputId]="id()"
                [ngModel]="value()"
                [selectionMode]="selectionMode()"
                [dateFormat]="dateFormat()"
                [placeholder]="floatLabel() ? '' : placeholder()"
                [disabled]="isDisabled()"
                [required]="required()"
                [readonlyInput]="readonlyInput()"
                [showIcon]="true"
                [iconDisplay]="resolvedIconDisplay()"
                [showTime]="showTime()"
                [timeOnly]="timeOnly()"
                [showButtonBar]="showButtonBar()"
                [minDate]="resolvedMinDate()"
                [maxDate]="resolvedMaxDate()"
                [disabledDates]="resolvedDisabledDates()"
                [disabledDays]="resolvedDisabledDays()"
                [view]="view()"
                [numberOfMonths]="numberOfMonths()"
                [inline]="inline()"
                [variant]="variant()"
                [fluid]="fluid()"
                [size]="sizeForPrimeng()"
                [appendTo]="appendTo()"
                [class]="computedClass()"
                [class.ng-invalid]="showsInvalid()"
                [class.ng-dirty]="showsInvalid()"
                (ngModelChange)="onValueChange($event)"
                (onFocus)="onFocus.emit($event)"
                (onBlur)="handleBlur($event)"
                (onSelect)="onSelect.emit($event)"
                (onClear)="onClear.emit()"
            >
                <ng-template #inputicon let-clickCallBack="clickCallBack">
                    <span class="fc-font-icon fc-font-icon-calendar" (click)="clickCallBack($event)"></span>
                </ng-template>
                <ng-template #triggericon>
                    <span class="fc-font-icon fc-font-icon-calendar"></span>
                </ng-template>
            </p-datepicker>
        </ng-template>

        @if (floatLabel()) {
            <fcp-float-label [variant]="floatLabelVariant()">
                <ng-container *ngTemplateOutlet="datePickerTemplate" />
                <label [for]="id()">{{ placeholder() }}</label>
            </fcp-float-label>
        } @else {
            <ng-container *ngTemplateOutlet="datePickerTemplate" />
        }
    `
})
export class FcpDatePickerComponent implements ControlValueAccessor {
    readonly defaultDateFormat = 'mm/dd/yy';

    /* Inputs: identification & core */
    e2e = input<string | null>(null);
    id = input<string | undefined>(undefined);
    placeholder = input<string>();
    selectionMode = input<DatePickerSelectionMode>(DatePickerSelectionMode.Single);
    dateFormat = input<string>(this.defaultDateFormat);
    disabled = input(false);
    required = input(false);
    readonlyInput = input(false);
    invalid = input(false);

    /* Inputs: display & layout */
    showIcon = input(true);
    iconDisplay = input<DatePickerIconDisplay>(DatePickerIconDisplay.Input);
    showTime = input(false);
    timeOnly = input(false);
    showButtonBar = input(false);
    inline = input(false);
    variant = input<DatePickerVariant>(DatePickerVariant.Outlined);
    fluid = input(false);
    size = input<DatePickerSize>();
    class = input<string>();
    appendTo = input<string | HTMLElement>('body');

    /* Inputs: date constraints (Date or ISO string, e.g. from Storybook date control) */
    minDate = input<Date | string>();
    maxDate = input<Date | string>();
    disabledDates = input<(Date | string)[]>();
    disabledDays = input<number[]>();

    /* Inputs: navigation & view */
    view = input<DatePickerView>(DatePickerView.Date);
    numberOfMonths = input(1);

    /* Inputs: float label */
    floatLabel = input<boolean>(false);
    floatLabelVariant = input<FloatLabelVariant>(FloatLabelVariant.In);

    /* Outputs */
    onFocus = output<Event>();
    onBlur = output<Event>();
    onSelect = output<DatePickerValue>();
    onClear = output<void>();

    constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    private _formDisabled = signal<boolean>(false);

    protected value = signal<DatePickerValue>(null);
    protected onChange: (value: DatePickerValue) => void = () => {};
    protected onTouched: () => void = () => {};

    isDisabled = computed(() => this.disabled() || this._formDisabled());
    protected computedClass = computed(() => {
        const baseClass = 'fcp-date-picker';
        const customClass = this.class();
        return customClass ? `${baseClass} ${customClass}` : baseClass;
    });
    protected resolvedIconDisplay = computed(() =>
        this.iconDisplay() === DatePickerIconDisplay.Input ? 'input' : 'button'
    );
    /** Only pass 'small' | 'large' to PrimeNG; undefined when Normal or not set (same as omitting the binding). */
    protected sizeForPrimeng = computed(() => {
        const size = this.size();
        return size === DatePickerSize.Small ? 'small' : size === DatePickerSize.Large ? 'large' : undefined;
    });
    protected resolvedMinDate = computed(() => this.toDate(this.minDate()));
    protected resolvedMaxDate = computed(() => this.toDate(this.maxDate()));
    protected resolvedDisabledDates = computed(() => {
        const raw = this.disabledDates();
        const arr = Array.isArray(raw) ? raw : [];

        return arr.map((d) => this.toDate(d)).filter((d): d is Date => d != null);
    });
    protected resolvedDisabledDays = computed(() => {
        const raw = this.disabledDays();

        return Array.isArray(raw) ? raw : [];
    });

    protected showsInvalid(): boolean {
        return this.invalid() || this.isControlInvalidAndDirty;
    }

    private get isControlInvalidAndDirty(): boolean {
        const control = this.control;
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    writeValue(value: DatePickerValue): void {
        this.value.set(this.normalizeValue(value));
    }
    registerOnChange(fn: (value: DatePickerValue) => void): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    protected onValueChange(value: DatePickerValue): void {
        const normalizedValue = this.normalizeValue(value);
        this.value.set(normalizedValue);
        this.onChange(normalizedValue);
    }

    protected handleBlur(event: Event): void {
        this.onBlur.emit(event);
        this.deferTouched();
    }

    /** Defers onTouched to next tick so a date selection (ngModelChange) can run first and avoid flashing invalid state. */
    protected deferTouched(): void {
        queueMicrotask(() => this.onTouched());
    }

    private get control() {
        return this.ngControl?.control ?? null;
    }

    private toDate(value: Date | string | undefined): Date | undefined {
        if (value == null) return undefined;
        if (value instanceof Date) return value;
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? undefined : d;
    }

    private normalizeValue(value: DatePickerValue): DatePickerValue {
        const mode = this.selectionMode();

        if (value === null || value === undefined) {
            return mode === DatePickerSelectionMode.Multiple || mode === DatePickerSelectionMode.Range ? [] : null;
        }

        switch (mode) {
            case DatePickerSelectionMode.Multiple:
                return Array.isArray(value) ? value : [value as Date];
            case DatePickerSelectionMode.Range:
                return Array.isArray(value) ? value.slice(0, 2) : [];
            case DatePickerSelectionMode.Single:
            default:
                return Array.isArray(value) ? (value[0] ?? null) : (value as Date);
        }
    }
}

export type DatePickerValue = Date | Date[] | null;
export enum DatePickerSelectionMode {
    Single = 'single',
    Multiple = 'multiple',
    Range = 'range'
}
export enum DatePickerView {
    Date = 'date',
    Month = 'month',
    Year = 'year'
}
export enum DatePickerVariant {
    Filled = 'filled',
    Outlined = 'outlined'
}
export enum DatePickerSize {
    Small = 'small',
    Normal = null,
    Large = 'large'
}

/**
 * Icon display mode:
 *   - button (default) = icon as separate trigger;
 *   - input = icon inside the input.
 *
 * See PrimeNG DatePicker Icon docs.
 * */
export enum DatePickerIconDisplay {
    Button = 'button',
    Input = 'input'
}
